import { formatDate, formatDateTimeSeconds, parseUtcMs } from "@/utils/datetime";
import {
  EditIcon,
  GithubIcon,
  LabelChip,
  SingleSelectDropdown,
  Text,
  type DataTableColumn,
} from "@carbon6/c6-frontend-design";
import type { KeyboardEvent, ReactNode } from "react";
import type { UserRole } from "@/app/userRole";
import { hatcheryDropdownModes, hatcheryTextModes } from "@/app/theme";
import { HatTextTooltip } from "@/components/tooltip";
import { memberTierOptions } from "./memberTier";
import type { OrgMemberTableRow } from "./OrgMemberTableRow";
import styles from "./AdminMembersPage.module.css";

// 그룹 칩 색(registry 축 teal). 출처는 칩 variant(solid/outline)로 구분한다.
const groupChipColor = 0x0d9488;
// 학습 워크스페이스 칩 색. 중립 slate.
const workspaceChipColor = 0x64748b;
// 가입 여부 칩 색. 가입=green, 미가입=muted gray.
const registeredChipColor = 0x16a34a;
const unregisteredChipColor = 0x6b7280;

// 그룹 칩은 내용 길이만큼 넓어진다. c6 LabelChip 기본 max-width:100%·flex 축소를 해제해
// 텍스트 ellipsis 를 막는다.
const groupChipStyle = { maxWidth: "none", flexShrink: 0 } as const;

export type OrgMemberColumnsOptions = {
  // 현재 로그인 멤버 uuid. 본인 행 등급 변경을 막는다(self-lockout 방지).
  currentMemberUuid: string | null;
  // 등급 변경이 진행 중이면 모든 행의 드롭다운을 잠가 경합을 막는다.
  busy: boolean;
  onSelectTier: (row: OrgMemberTableRow, tier: UserRole) => void;
  // 그룹/Workspace 매핑 컬럼을 클릭하면 Edit 모달을 연다.
  onEditGroups: (row: OrgMemberTableRow) => void;
  onEditWorkspaces: (row: OrgMemberTableRow) => void;
};

function emptyDash(): ReactNode {
  return (
    <Text variant={hatcheryTextModes["body-normal"]} className={styles.muted}>
      —
    </Text>
  );
}

export function createOrgMemberColumns({
  currentMemberUuid,
  busy,
  onSelectTier,
  onEditGroups,
  onEditWorkspaces,
}: OrgMemberColumnsOptions): readonly DataTableColumn<OrgMemberTableRow>[] {
  return [
    {
      key: "githubLogin",
      header: "GitHub 유저",
      width: "minmax(180px, 1fr)",
      render: (row) => (
        <div className={styles.nameCell}>
          <span className={styles.githubMark} aria-hidden="true">
            <GithubIcon />
          </span>
          <Text variant={hatcheryTextModes["body-bold"]}>{row.githubLogin}</Text>
          {row.memberUuid !== null && row.memberUuid === currentMemberUuid ? (
            <span className={styles.selfBadge}>나</span>
          ) : null}
        </div>
      ),
    },
    {
      key: "tier",
      header: "역할",
      width: "104px",
      // 가입자만 역할을 매핑할 수 있다(미가입은 가입 전이라 비활성). 본인 행은 self-lockout 을
      // 막으려고 잠근다.
      render: (row) =>
        row.registered && row.tier !== null ? (
          <div className={styles.tierCell}>
            <SingleSelectDropdown
              mode={hatcheryDropdownModes.default}
              label="역할"
              value={row.tier}
              options={memberTierOptions}
              className={styles.roleDropdown}
              disabled={(row.memberUuid !== null && row.memberUuid === currentMemberUuid) || busy}
              onValueChange={(next) => onSelectTier(row, next as UserRole)}
            />
          </div>
        ) : (
          emptyDash()
        ),
    },
    {
      key: "registryGroups",
      header: "그룹",
      width: "minmax(220px, 1.4fr)",
      // 클릭 가능한 칩(interactive variant: 평소 filled, hover 시 outline). 편집 표시는 칩
      // 아이콘(EditIcon) 하나로 한다. 출처(직접 지정/GitHub 자동)는 title 툴팁이 표시한다.
      // 워크스페이스 매핑은 소속 그룹에 의존한다. 그래서 그룹 컬럼을 워크스페이스 바로 앞에 둔다.
      render: (row) => renderGroupSummary(row, onEditGroups),
    },
    {
      key: "memberUuid",
      header: "Training Workspace",
      width: "160px",
      // 워크스페이스 매핑은 가입자만 할 수 있다(member_uuid 필요). 클릭 가능한 칩("X"/"X 외 N개")으로
      // 보여준다. 편집 표시는 칩 아이콘 하나로 한다. 칩을 클릭하면 모달에서 편집한다.
      render: (row) =>
        row.registered ? renderWorkspaceSummary(row, onEditWorkspaces) : emptyDash(),
    },
    {
      key: "registered",
      header: "가입 여부",
      width: "110px",
      render: (row) => (
        <LabelChip
          color={row.registered ? registeredChipColor : unregisteredChipColor}
          size="xs"
          variant="filled"
          showIcon={false}
        >
          {row.registered ? "가입" : "미가입"}
        </LabelChip>
      ),
    },
    {
      key: "lastLoginAt",
      header: "마지막 로그인",
      width: "150px",
      // 셀에는 날짜만 표시한다. hover 시 전체 시각(초까지)을 툴팁으로 보여준다.
      render: (row) =>
        row.lastLoginAt !== null ? (
          <HatTextTooltip text={formatDateTimeSeconds(parseUtcMs(row.lastLoginAt))} placement="top">
            <span className={styles.muted}>{formatDate(parseUtcMs(row.lastLoginAt))}</span>
          </HatTextTooltip>
        ) : (
          emptyDash()
        ),
    },
  ];
}

// 클릭 가능한 칩/텍스트 공통 props. span 이 버튼처럼(role/tabIndex/Enter·Space) 동작하게 한다.
// LabelChip 은 onClick 등 DOM 핸들러를 chip span 에 그대로 전달한다(...rest). 그래서 래퍼 버튼이
// 필요 없다.
function clickableProps(onActivate: () => void, ariaLabel: string) {
  return {
    role: "button" as const,
    tabIndex: 0,
    "aria-label": ariaLabel,
    title: "멤버 정보를 편집하려면 클릭해 주세요.",
    onClick: onActivate,
    onKeyDown: (event: KeyboardEvent<HTMLSpanElement>) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        onActivate();
      }
    },
  };
}

// "매핑 추가". 매핑이 없을 때의 클릭 가능한 빈 상태다(모달을 열어 추가한다).
function renderAddHint(onActivate: () => void, ariaLabel: string): ReactNode {
  return (
    <span {...clickableProps(onActivate, ariaLabel)} className={styles.addHint}>
      매핑 추가
    </span>
  );
}

// 그룹 셀 요약. 클릭 가능한 첫 그룹 칩 + "외 N개". 매핑이 없으면 "매핑 추가" 를 표시한다.
// 칩 규칙:
//   - interactive: 평소 filled, hover 시 outline.
//   - 편집 표시는 칩 아이콘 하나로 한다.
//   - 출처는 title 툴팁이 표시한다.
function renderGroupSummary(
  row: OrgMemberTableRow,
  onEdit: (row: OrgMemberTableRow) => void,
): ReactNode {
  const ariaLabel = `${row.githubLogin} 그룹 매핑 편집`;
  if (row.registryGroups.length === 0) {
    return renderAddHint(() => onEdit(row), ariaLabel);
  }
  const [first, ...rest] = row.registryGroups;
  const explicit = first.source === "explicit";
  return (
    <span className={styles.groupSummary}>
      <LabelChip
        {...clickableProps(() => onEdit(row), ariaLabel)}
        color={groupChipColor}
        size="xs"
        variant="interactive"
        icon={<EditIcon />}
        style={groupChipStyle}
        title={
          explicit
            ? "관리자가 직접 지정한 그룹입니다."
            : "GitHub 팀 소속에 따라 자동으로 매핑된 그룹입니다."
        }
      >
        {first.name}
      </LabelChip>
      {rest.length > 0 ? (
        <Text variant={hatcheryTextModes["body-normal"]} className={styles.summaryMore}>
          외 {rest.length}개
        </Text>
      ) : null}
    </span>
  );
}

// 그룹 미지정 행의 비활성 "매핑 추가". 워크스페이스 후보·생성은 소속 그룹에서 파생된다.
// 그래서 그룹 매핑이 먼저 있어야 한다. 클릭을 막고 툴팁으로 순서를 안내한다.
function renderDisabledAddHint(): ReactNode {
  return (
    <HatTextTooltip text="그룹을 먼저 추가해 주세요" placement="top">
      <span className={styles.addHintDisabled} aria-disabled="true">
        매핑 추가
      </span>
    </HatTextTooltip>
  );
}

// 워크스페이스 셀 요약. 클릭 가능한 첫 이름 칩 + "외 N개". 편집 표시는 칩 아이콘 하나로 한다.
// 매핑이 없으면 "매핑 추가" 를 표시한다. 단, 소속 그룹이 없으면 비활성으로 두고 그룹을 먼저
// 매핑하라고 안내한다.
function renderWorkspaceSummary(
  row: OrgMemberTableRow,
  onEdit: (row: OrgMemberTableRow) => void,
): ReactNode {
  const ariaLabel = `${row.githubLogin} Training Workspace 매핑 편집`;
  if (row.workspaces.length === 0) {
    if (row.registryGroups.length === 0) {
      return renderDisabledAddHint();
    }
    return renderAddHint(() => onEdit(row), ariaLabel);
  }
  const [first, ...rest] = row.workspaces;
  return (
    <span className={styles.groupSummary}>
      <LabelChip
        {...clickableProps(() => onEdit(row), ariaLabel)}
        color={workspaceChipColor}
        size="xs"
        variant="interactive"
        icon={<EditIcon />}
        style={groupChipStyle}
      >
        {first.workspace_name}
      </LabelChip>
      {rest.length > 0 ? (
        <Text variant={hatcheryTextModes["body-normal"]} className={styles.summaryMore}>
          외 {rest.length}개
        </Text>
      ) : null}
    </span>
  );
}
